import { useState, useCallback, useMemo } from "react";
import { Tag, Tooltip } from "@narmi/design_system";
import { useSpring, animated } from "@react-spring/web";
import { catcat as cc } from "cerulean";
import { entities } from "byzantine";
import Utils from "byzantine/src/utils";
import { format } from "date-fns";

import { useInstitutionSettings } from "../../../contexts/InstitutionSettingsContext";
import { useSudoContext } from "../../../SudoContext";

import styles from "./CardImage.module.scss";
import { CARD_LOGOS, CARD_ASPECT_RATIOS, LOGO_SIZE_CONFIG } from "./constants";
import { CardImageProps } from "./types";

interface SensitiveCardInfoToggleButtonProps {
  isFlipped: boolean;
  onClick: () => void;
  textColor: string;
  isLocked: boolean;
  isDisabled: boolean;
}

const SensitiveCardInfoToggleButton = ({
  textColor,
  isFlipped,
  isLocked,
  onClick,
  isDisabled,
}: SensitiveCardInfoToggleButtonProps) => (
  <button
    className={cc([
      styles.sensitiveCardInfoBtn,
      isLocked && styles.sensitiveCardInfoBtnHidden,
    ])}
    onClick={onClick}
    aria-label={isFlipped ? "Hide sensitive info" : "Show sensitive info"}
    disabled={isDisabled}
  >
    <span
      className={isFlipped ? "narmi-icon-eye-off" : "narmi-icon-eye"}
      style={{ color: textColor }}
    />
  </button>
);

const useCopyToClipboard = (text: string | null) => {
  const [tooltipText, setTooltipText] = useState("Click to copy");

  const copyText = useCallback(() => {
    if (text) {
      navigator.clipboard.writeText(text);
      setTooltipText("Copied");
    }
  }, [text]);

  const resetTooltip = useCallback(() => {
    setTimeout(() => {
      // Delay resetting tooltip to avoid flickering
      setTooltipText("Click to copy");
    }, 1000);
  }, []);

  return { copyText, resetTooltip, tooltipText };
};

const CardImage = ({
  name,
  textColor,
  isLocked,
  image,
  cardId,
  lastFourDigits,
  network,
  size = "small",
  showSensitiveInfoButton = false,
}: CardImageProps) => {
  // We can't just use "loading" from useSensitiveCardInfo because
  // there needs to be a sudo check before that request even happens.
  // This state will encompass both requests.
  const [isLoadingSensitiveCardInfo, setIsLoadingSensitiveCardInfo] =
    useState(false);
  const [isFlipped, setIsFlipped] = useState(false);
  const [isHalfway, setIsHalfway] = useState(false);

  const { sudo_mode_required_for_sensitive_card_info: sudoRequired } =
    useInstitutionSettings();
  const { establishSudo, setOnCancel } = useSudoContext();
  const { data: sensitiveInfo, send: fetchSensitiveCardInfo } =
    entities.sensitiveCardInfo.useSensitiveCardInfo(cardId);

  const onSudoCancel = useCallback(() => {
    setIsLoadingSensitiveCardInfo(false);
  }, []);

  const onSensitiveInfoBtnClick = useCallback(() => {
    const fetchInfo = async () => {
      if (!sensitiveInfo) {
        await fetchSensitiveCardInfo();
      }
      setIsFlipped(true);
      setIsLoadingSensitiveCardInfo(false);
    };

    if (!isFlipped) {
      setIsLoadingSensitiveCardInfo(true);
      if (sudoRequired) {
        setOnCancel(onSudoCancel);
        establishSudo(`/cards/${cardId}`, fetchInfo);
      } else {
        fetchInfo();
      }
    } else {
      setIsFlipped(false);
    }
  }, [
    establishSudo,
    fetchSensitiveCardInfo,
    isFlipped,
    cardId,
    sensitiveInfo,
    sudoRequired,
  ]);

  const isCardInfoVisible = !isLocked && isFlipped;

  const { transform } = useSpring({
    progress: isCardInfoVisible ? 1 : 0,
    onChange: ({ value: { progress } }: { value: { progress: number } }) => {
      setIsHalfway(progress >= 0.5);
    },
    transform: `rotateY(${isCardInfoVisible ? 180 : 0}deg)`,
    config: { mass: 1, tension: 280, friction: 20, duration: 400 },
  });

  const cardNetwork = network in CARD_LOGOS ? network : "Visa";
  const logo = CARD_LOGOS[cardNetwork];

  const aspectRatio = CARD_ASPECT_RATIOS[cardNetwork];
  const logoSizeSettings = LOGO_SIZE_CONFIG[size];
  const networkSizeSettings =
    logoSizeSettings[cardNetwork.toLowerCase() as "visa" | "mastercard"];

  const width: `${number}px` = logoSizeSettings.logoSize;
  const height = `${parseInt(width, 10) / aspectRatio}px`;

  const cardLogoStyle = {
    backgroundImage: `url('${logo}')`,
    width,
    height,
    ...networkSizeSettings,
  };

  const cardSideStyles = {
    color: textColor,
    backgroundImage: `url('${image}')`,
    backgroundSize: "cover",
    backgroundRepeat: "no-repeat",
    transition: "filter 0.3s ease-in-out",
    filter: isLocked ? "opacity(0.4)" : "",
  };

  const sizeClass = size === "large" ? styles.large : styles.small;
  const cardLogoImg = <div className={styles.cardLogo} style={cardLogoStyle} />;

  const cardNumber = useMemo(() => {
    if (!sensitiveInfo?.pan) {
      return null;
    }
    return Utils.formatCardNumber(sensitiveInfo?.pan);
  }, [sensitiveInfo?.pan]);

  const expirationDate = useMemo(() => {
    if (!sensitiveInfo?.expiration_date) {
      return null;
    }
    return format(new Date(sensitiveInfo?.expiration_date), "MM/yy");
  }, [sensitiveInfo?.expiration_date]);

  const {
    copyText: copyCardNumber,
    resetTooltip,
    tooltipText,
  } = useCopyToClipboard(cardNumber);

  return (
    <div
      className={cc(styles.cardWrapper, { [styles.large]: size === "large" })}
    >
      <animated.div
        className={cc(styles.cardContainer, sizeClass)}
        style={{ transform }}
      >
        {/* Back Side */}
        <div
          className={cc(styles.cardBack, { [styles.hidden]: !isHalfway })}
          style={cardSideStyles}
        >
          {sensitiveInfo && (
            <div className={styles.sensitiveInfo}>
              <div className={styles.infoRow}>
                <div className={styles.info}>
                  <div className={styles.infoLabel}>Card number</div>
                  {cardNumber && (
                    <span
                      role="button"
                      className={styles.copyableCardNumber}
                      onClick={copyCardNumber}
                      onMouseLeave={resetTooltip}
                    >
                      <Tooltip text={tooltipText}>{cardNumber}</Tooltip>
                    </span>
                  )}
                </div>
              </div>
              <div className={styles.infoRow}>
                <div className={styles.info}>
                  <div className={styles.infoLabel}>Expiration</div>
                  <div className={styles.infoValue}>{expirationDate}</div>
                </div>
                <div className={styles.info}>
                  <div className={styles.infoLabel}>CVV</div>
                  <div className={styles.infoValue}>{sensitiveInfo.cvv}</div>
                </div>
                <div className={styles.info}>
                  <SensitiveCardInfoToggleButton
                    isDisabled={isLoadingSensitiveCardInfo}
                    isLocked={isLocked}
                    textColor={textColor}
                    isFlipped={isFlipped}
                    onClick={onSensitiveInfoBtnClick}
                  />
                </div>
              </div>
            </div>
          )}
          {cardLogoImg}
        </div>
        {/* Front Side */}
        <div
          className={cc(styles.cardSvg, { [styles.hidden]: isHalfway })}
          style={cardSideStyles}
        >
          {name && <span className={styles.cardName}>{name}</span>}
          {lastFourDigits && (
            <>
              <span className={styles.mask}>**</span>
              <span className={styles.lastFour}>{lastFourDigits}</span>
              {showSensitiveInfoButton && !isLocked && (
                <SensitiveCardInfoToggleButton
                  isDisabled={isLoadingSensitiveCardInfo}
                  isLocked={isLocked}
                  textColor={textColor}
                  isFlipped={isFlipped}
                  onClick={onSensitiveInfoBtnClick}
                />
              )}
            </>
          )}
          {cardLogoImg}
        </div>

        <div
          aria-hidden={!isLocked}
          className={cc([
            styles.lockedTag,
            !isLocked && styles.lockedTagHidden,
          ])}
        >
          <Tag label="Locked" />
        </div>
      </animated.div>
    </div>
  );
};

export default CardImage;
